import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageSquare, RefreshCw, ChevronDown, ChevronUp, Send } from "lucide-react";

const STATUS_STYLES = {
  sent: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  failed: "bg-red-500/10 text-red-400 border-red-500/30",
  skipped: "bg-zinc-700/40 text-zinc-400 border-zinc-600/40",
};

const REASON_LABELS = {
  brevo_ip_not_whitelisted: "whitelist server IP in Brevo",
  invalid_api_key: "bad Brevo API key",
  sender_not_registered: "verify sender in Brevo",
  quota_exceeded: "Brevo quota exceeded",
  no_api_key: "email not configured",
  no_recipient: "no email address",
  not_au_mobile: "not an AU mobile (04…)",
  sms_not_configured: "SMS not configured",
  sms_daily_cap: "daily SMS limit for this number reached (spam guard)",
};

function fmtWhen(iso) {
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function reasonLabel(reason) {
  if (!reason || reason === "ok") return "";
  if (reason === "ok_links_stripped") return "sent without links — Texto account doesn't allow URLs yet";
  if (reason.startsWith("texto:")) {
    const code = reason.slice(6);
    if (code === "insufficient_credits") return "Texto out of credit — top up your Texto account";
    if (code === "invalid_api_key") return "bad Texto API key";
    return `Texto: ${code.replace(/_/g, " ")}`;
  }
  if (reason.startsWith("clicksend:")) {
    const code = reason.slice(10);
    if (code === "INSUFFICIENT_CREDIT") return "ClickSend out of credit — top up your ClickSend account";
    return `ClickSend: ${code.replace(/_/g, " ").toLowerCase()}`;
  }
  return REASON_LABELS[reason] || reason.replace(/^(error|network):/, "").slice(0, 80);
}

export default function CommsLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/comms-log");
      setRows(r.data);
    } catch {
      toast.error("Could not load communications log");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="comms-log-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 border-l-2 border-brand-red pl-4">
          <Send className="h-5 w-5 text-brand-red mt-0.5" />
          <div>
            <h2 className="text-lg font-bold text-white">Communications log</h2>
            <p className="text-sm text-zinc-400">
              Every SMS and email the site has attempted to send — newest first. Tap a row to see the full message.
            </p>
          </div>
        </div>
        <button
          data-testid="comms-refresh-btn"
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:border-brand-red hover:text-brand-red transition-colors"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {!loading && rows.length === 0 && (
        <p className="text-sm text-zinc-500 py-8 text-center" data-testid="comms-empty">
          Nothing sent yet. Entries appear here the moment a booking email or SMS goes out.
        </p>
      )}

      <div className="divide-y divide-zinc-900 border border-zinc-900" data-testid="comms-log-list">
        {rows.map((r) => {
          const open = openId === r.id;
          return (
            <div key={r.id} data-testid={`comms-row-${r.id}`}>
              <button
                data-testid={`comms-row-toggle-${r.id}`}
                onClick={() => setOpenId(open ? null : r.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-zinc-900/50 transition-colors"
              >
                <span className={`shrink-0 h-8 w-8 flex items-center justify-center border ${r.kind === "sms" ? "border-sky-500/30 text-sky-400" : "border-amber-500/30 text-amber-400"}`}>
                  {r.kind === "sms" ? <MessageSquare className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-white truncate">
                    {r.purpose || r.subject || (r.kind === "sms" ? "SMS" : "Email")}
                    {r.customer_name ? <span className="text-zinc-500"> — {r.customer_name}</span> : null}
                  </span>
                  <span className="block text-xs text-zinc-500 truncate">
                    {r.kind === "sms" ? "SMS" : "Email"} to {r.to || "—"} · {fmtWhen(r.created_at)}
                  </span>
                </span>
                <span
                  data-testid={`comms-status-${r.id}`}
                  className={`shrink-0 px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider border ${STATUS_STYLES[r.status] || STATUS_STYLES.skipped}`}
                >
                  {r.status === "sent" ? "Sent ✓" : r.status === "failed" ? "Failed ✗" : "Skipped"}
                </span>
                {open ? <ChevronUp className="h-4 w-4 text-zinc-500 shrink-0" /> : <ChevronDown className="h-4 w-4 text-zinc-500 shrink-0" />}
              </button>
              {open && (
                <div className="px-4 pb-4 pl-[60px]" data-testid={`comms-body-${r.id}`}>
                  {reasonLabel(r.reason) && (
                    <p className={`text-xs mb-2 font-mono ${r.status === "sent" ? "text-amber-400" : "text-red-400"}`}>
                      {r.status === "sent" ? "Note" : "Reason"}: {reasonLabel(r.reason)}
                    </p>
                  )}
                  {r.subject && <p className="text-xs text-zinc-400 mb-1"><span className="text-zinc-600">Subject:</span> {r.subject}</p>}
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap break-words bg-zinc-900/60 border border-zinc-800 p-3">
                    {r.body || "(empty message)"}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
